// Client record → "Internal Audit" section: one row per separately-audited system (e.g. DVI = ISO 45001 row +
// ISO/IEC 27001 row). The last internal audit date drives the 6 / 9 / 11-month reminders.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { iaStandardOptions, standardsLabel, sgToday } from '../lib/service'
import { iaAction, longDate } from '../lib/iaReport'
import { toast, failed } from '../lib/toast'
import { useAuth } from '../lib/AuthContext'
import IAContactLine from './IAContactLine'
import { groupCompanyText as companyText } from './IAGroupLine'

const blank = { standards: [], last_ia_date: '', certification_body: '', cert_expiry: '', scope_of_certification: '', next_external_audit_date: '',
  contract_until: '', contract_audits_left: '', contract_quote_ref: '', hasContract: false, group: [] }
// Group audit (owner, 5 Nov 2026): other companies audited together with this record's company — one audit, one job,
// one report. Each stays its own client; the links live in ia_group_companies (removal is dated, never deleted).
// Any contract detail recorded → the "multi-year contract" box opens ticked.
const hasContractData = c => !!(c.contract_until || (c.contract_audits_left != null && c.contract_audits_left !== '') || c.contract_quote_ref)
// Multi-year contract still running: audits left and not past its end date.
const onContract = c => (c.contract_audits_left || 0) > 0 && (!c.contract_until || c.contract_until >= sgToday())
const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2)) + Date.now().toString(36)

export default function ClientIACycles({ client }) {
  const { profile } = useAuth()
  const [cycles, setCycles] = useState(null)
  const [groups, setGroups] = useState({})    // cycle id → [{ linkId, client_id, name, uen }]
  const [memberOf, setMemberOf] = useState(null)   // this client is in another company's group audit → { name }
  const [edit, setEdit] = useState(null)      // cycle being edited / added
  const [saving, setSaving] = useState(false)
  const [find, setFind] = useState('')        // "Add a company" search
  const [found, setFound] = useState([])

  async function load() {
    const { data, error } = await supabase.from('client_ia_cycles').select('*').eq('client_id', client.id).order('created_at')
    const list = error ? [] : (data || [])
    const ids = list.map(c => c.id)
    const [{ data: links }, { data: mine }] = await Promise.all([
      ids.length ? supabase.from('ia_group_companies').select('id, cycle_id, client_id, sort_order, clients(name, uen)').in('cycle_id', ids).is('removed_at', null).order('sort_order')
        : Promise.resolve({ data: [] }),
      supabase.from('ia_group_companies').select('id, client_ia_cycles(client_id, clients(name))').eq('client_id', client.id).is('removed_at', null).limit(1),
    ])
    const g = {}
    for (const l of links || []) (g[l.cycle_id] = g[l.cycle_id] || []).push({ linkId: l.id, client_id: l.client_id, name: l.clients?.name || '—', uen: l.clients?.uen || '' })
    setGroups(g)
    setMemberOf(mine?.[0] ? { name: mine[0].client_ia_cycles?.clients?.name || 'another company' } : null)
    setCycles(list)
  }
  useEffect(() => { load() }, [client.id])

  // "Add a company": search the client list by name or UEN (not this company, not ones already in the list).
  useEffect(() => {
    const q = find.replace(/[,()%*]/g, ' ').trim()
    if (q.length < 2) { setFound([]); return }
    let live = true
    const t = setTimeout(async () => {
      const { data } = await supabase.from('clients').select('id, name, uen').or(`name.ilike.*${q}*,uen.ilike.*${q}*`).order('name').limit(8)
      const taken = new Set([client.id, ...((edit?.group) || []).map(x => x.client_id)])
      if (live) setFound((data || []).filter(c => !taken.has(c.id)))
    }, 250)
    return () => { live = false; clearTimeout(t) }
  }, [find, edit?.group, client.id])

  // Save the group list with the record: add new companies, date-stamp removed ones. The database refuses a company
  // that is already in another group or has its own active internal audit record (message shown as it comes).
  async function saveGroup(cycleId) {
    const before = groups[cycleId] || []
    const after = edit.group || []
    const who = profile?.name || 'Office'
    const removed = before.filter(b => !after.some(a => a.client_id === b.client_id))
    const added = after.filter(a => !before.some(b => b.client_id === a.client_id))
    for (const r of removed) {
      const { error } = await supabase.from('ia_group_companies').update({ removed_at: new Date().toISOString(), removed_by: who }).eq('id', r.linkId)
      if (error) throw new Error(`${r.name}: ${error.message}`)
    }
    for (const a of added) {
      const { error } = await supabase.from('ia_group_companies').insert({ cycle_id: cycleId, client_id: a.client_id, sort_order: after.indexOf(a) + 1, added_by: who })
      if (error) throw new Error(`${a.name}: ${error.message}`)
    }
  }

  async function save() {
    if (!edit.standards.length) return toast('Tick at least one standard', 'warning')
    const k = edit.hasContract   // unticked → no contract (all three cleared)
    if (k && (!edit.contract_until || edit.contract_audits_left === '' || edit.contract_audits_left == null))
      return toast('Fill in the contract dates', 'warning', { detail: '"Contract until" and "Audits left" — or untick multi-year' })
    setSaving(true)
    const row = {
      client_id: client.id, standards: edit.standards, last_ia_date: edit.last_ia_date || null,
      certification_body: (edit.certification_body || '').trim() || null, cert_expiry: edit.cert_expiry || null,
      scope_of_certification: (edit.scope_of_certification || '').trim() || null, next_external_audit_date: edit.next_external_audit_date || null,
      contract_until: k ? edit.contract_until || null : null,
      contract_audits_left: !k || edit.contract_audits_left === '' || edit.contract_audits_left == null ? null : Math.max(0, parseInt(edit.contract_audits_left, 10) || 0),
      contract_quote_ref: k ? (edit.contract_quote_ref || '').trim() || null : null,
      updated_at: new Date().toISOString(),
    }
    const { data: savedRow, error } = edit.id
      ? await supabase.from('client_ia_cycles').update(row).eq('id', edit.id).select('id').single()
      : await supabase.from('client_ia_cycles').insert(row).select('id').single()
    if (error) { setSaving(false); return failed("Couldn't save", error.message) }
    try { await saveGroup(savedRow.id) } catch (e) {
      setSaving(false); load()
      return failed('Saved, but the group audit list was not fully updated', e.message)
    }
    setSaving(false)
    setEdit(null); setFind(''); load()
  }

  async function copyLink(c) {
    if (!c.active) return toast('Restart reminders first', 'warning', { detail: 'Reminders are stopped for this standard' })
    const token = newToken()
    const { error } = await supabase.from('ia_form_links').insert({ token, cycle_id: c.id, source: 'manual' })
    if (error) return failed("Couldn't create the link", error.message)
    const url = `${window.location.origin}/ia/${token}`
    try { await navigator.clipboard.writeText(url); toast('Form link copied', 'success') } catch { window.prompt('Copy this link:', url) }
  }

  async function stop(c) {
    if (!window.confirm(`Stop internal audit reminders for ${standardsLabel(c.standards)}?`)) return
    try { await iaAction({ action: 'stop-cycle', cycleId: c.id }); load() } catch (e) { failed("Couldn't stop reminders", e.message) }
  }
  async function restart(c) {
    const { error } = await supabase.from('client_ia_cycles').update({ active: true, stopped_at: null, stopped_by: null, updated_at: new Date().toISOString() }).eq('id', c.id)
    if (error) return failed("Couldn't restart reminders", error.message)
    load()
  }

  if (cycles === null) return null
  const set = (k, v) => setEdit(e => ({ ...e, [k]: v }))
  return (
    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>Internal Audit</h3>
        {/* A company in another company's group audit gets no record of its own (remove it from that group first). */}
        {!edit && !memberOf && <button className="sm" onClick={() => setEdit({ ...blank })}>+ Add</button>}
      </div>
      {memberOf && <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>Group audit under {memberOf.name}</div>}
      {cycles.length === 0 && !edit && !memberOf && <div style={{ fontSize: 13, color: 'var(--text3)' }}>None</div>}
      {cycles.length > 0 && <div style={{ marginBottom: 6 }}><IAContactLine clientId={client.id} /></div>}
      {cycles.map(c => (
        <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, padding: '5px 0', flexWrap: 'wrap', opacity: c.active ? 1 : 0.6 }}>
          <div style={{ flex: '1 1 240px' }}>
            <b>{standardsLabel(c.standards) || '—'}</b>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              Last internal audit: {c.last_ia_date ? longDate(c.last_ia_date) : '—'}
              {c.certification_body ? ` · ${c.certification_body}` : ''}{c.cert_expiry ? ` · cert expires ${longDate(c.cert_expiry)}` : ''}
              {!c.active && ' · reminders stopped'}
            </div>
            {onContract(c) && <div style={{ fontSize: 13, color: 'var(--brand-dark)' }}>
              Contract{c.contract_until ? ` until ${longDate(c.contract_until)}` : ''} · {c.contract_audits_left} audit{c.contract_audits_left === 1 ? '' : 's'} left{c.contract_quote_ref ? ` · ${c.contract_quote_ref}` : ''}
            </div>}
            {(groups[c.id] || []).length > 0 && <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              Group audit: {groups[c.id].map(companyText).join(', ')}
            </div>}
          </div>
          <button className="sm" onClick={() => { setFind(''); setEdit({ ...blank, ...c, last_ia_date: c.last_ia_date || '', cert_expiry: c.cert_expiry || '', next_external_audit_date: c.next_external_audit_date || '',
            contract_until: c.contract_until || '', contract_audits_left: c.contract_audits_left ?? '', contract_quote_ref: c.contract_quote_ref || '', hasContract: hasContractData(c),
            group: (groups[c.id] || []).map(g => ({ ...g })) }) }}>Edit</button>
          <button className="sm" onClick={() => copyLink(c)}>Copy form link</button>
          {c.active ? <button className="sm" onClick={() => stop(c)}>Stop reminders</button> : <button className="sm" onClick={() => restart(c)}>Restart</button>}
        </div>))}
      {edit && (
        <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12, marginTop: 8 }}>
          <div className="field"><label>Standard(s)</label>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
              {(() => {
                const opts = iaStandardOptions([...((cycles || []).find(x => x.id === edit.id)?.standards || []), ...edit.standards])
                return opts.map(s => (
                  <label key={s} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ width: 'auto' }} checked={edit.standards.includes(s)}
                      onChange={() => set('standards', edit.standards.includes(s) ? edit.standards.filter(x => x !== s) : opts.filter(x => x === s || edit.standards.includes(x)))} /> {s}
                  </label>))
              })()}
            </div></div>
          <div className="grid-2">
            <div className="field"><label>Last internal audit date</label><input type="date" value={edit.last_ia_date} onChange={e => set('last_ia_date', e.target.value)} /></div>
            <div className="field"><label>Certification body</label><input value={edit.certification_body || ''} onChange={e => set('certification_body', e.target.value)} /></div>
            <div className="field"><label>Certificate expiry</label><input type="date" value={edit.cert_expiry} onChange={e => set('cert_expiry', e.target.value)} /></div>
            <div className="field"><label>Next external audit date</label><input type="date" value={edit.next_external_audit_date} onChange={e => set('next_external_audit_date', e.target.value)} /></div>
          </div>
          <div className="field"><label>Scope of certification</label><textarea rows={2} value={edit.scope_of_certification || ''} onChange={e => set('scope_of_certification', e.target.value)} /></div>
          {/* Only for a signed multi-year internal audit contract (filled in automatically when a contract quote is
              signed in AuditPM). Hidden unless ticked, so it is not filled in by mistake. */}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text)', cursor: 'pointer', margin: '4px 0 10px' }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!edit.hasContract} onChange={e => {
              if (!e.target.checked && hasContractData(edit) && !window.confirm('Remove the multi-year contract for this client?')) return
              set('hasContract', e.target.checked)
            }} />
            This client has a multi-year Internal Audit contract
          </label>
          {edit.hasContract && <div className="grid-2">
            <div className="field"><label>Contract until *</label><input type="date" value={edit.contract_until} onChange={e => set('contract_until', e.target.value)} /></div>
            <div className="field"><label>Audits left *</label><input type="number" min="0" value={edit.contract_audits_left} onChange={e => set('contract_audits_left', e.target.value)} onWheel={e => e.currentTarget.blur()} /></div>
            <div className="field"><label>Contract quote ref</label><input value={edit.contract_quote_ref || ''} onChange={e => set('contract_quote_ref', e.target.value)} placeholder="e.g. pm/1501-26/WT" /></div>
          </div>}
          {/* Group audit: other companies audited together with this one. Saved with the record (Save below). */}
          <div className="field">
            <label>Group audit companies</label>
            {(edit.group || []).length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>None</div>}
            {(edit.group || []).map(g => (
              <div key={g.client_id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '3px 0' }}>
                <span style={{ flex: 1 }}>{companyText(g)}</span>
                <button className="sm" onClick={() => set('group', edit.group.filter(x => x.client_id !== g.client_id))}>Remove</button>
              </div>))}
            <div style={{ position: 'relative', maxWidth: 420, marginTop: 4 }}>
              <input value={find} onChange={e => setFind(e.target.value)} placeholder="Search company name or UEN" />
              {found.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, marginTop: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                  {found.map(c => (
                    <div key={c.id} role="button" tabIndex={0} style={{ padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}
                      onMouseDown={e => { e.preventDefault(); set('group', [...(edit.group || []), { client_id: c.id, name: c.name, uen: c.uen || '' }]); setFind('') }}>
                      {companyText(c)}
                    </div>))}
                </div>)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="sm" onClick={() => { setEdit(null); setFind('') }}>Cancel</button>
          </div>
        </div>)}
    </div>
  )
}
